/**
 * MKS-315 — one way to render "logged vs. target" wherever the /time surfaces
 * show it: the weekly row and the monthly total in the month grid, the week
 * total in the week grid, and the summary panel under them.
 *
 * Before this, a weekly cell read `18.5 / 16` — no unit, no label, nothing
 * saying which number is which — and the monthly total `86.5h / 168h` wrapped
 * over three lines in the 48px Total column. Both now read `18.5 h of 16 h` on
 * one line, toned by whether the period is short of, on, or over its target.
 */

/** `18.5` for 18.5, `16` for 16 — a trailing `.0` is noise in a narrow cell. */
export function formatHoursValue(hours: number): string {
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}

/** The canonical one-line label, e.g. `18.5 h of 16 h`. */
export function formatLoggedVsTarget(logged: number, target: number): string {
  return `${formatHoursValue(logged)} h of ${formatHoursValue(target)} h`;
}

export type TargetTone = 'under' | 'met' | 'over';

/**
 * Hours are logged in quarters, so anything within 0.05 h of the target is
 * "on target" — a rounding artifact must not paint a full week amber.
 */
export function targetTone(logged: number, target: number): TargetTone {
  if (target <= 0) return 'met';
  if (logged < target - 0.05) return 'under';
  if (logged > target + 0.05) return 'over';
  return 'met';
}

/** Text colour per tone. Amber for short, emerald for on, blue for over. */
export const TARGET_TONE_CLASS: Record<TargetTone, string> = {
  under: 'text-amber-700',
  met: 'text-emerald-700',
  over: 'text-blue-700',
};

/** Native tooltip spelling out the comparison the colour only hints at. */
export function targetTitle(logged: number, target: number): string {
  const base = `Logged ${formatHoursValue(logged)} h of a ${formatHoursValue(target)} h target`;
  const tone = targetTone(logged, target);
  if (tone === 'met') return `${base} — on target`;
  const diff = Math.abs(logged - target);
  return `${base} — ${formatHoursValue(diff)} h ${tone === 'over' ? 'over' : 'short'}`;
}

/**
 * MKS-315 — the row's state, shown as a thin left border on the project name
 * instead of washing every cell in the row with the state colour. Most severe
 * wins: a returned entry is the one a person has to act on.
 */
export type RowState = 'open' | 'submitted' | 'approved' | 'rejected';

export function dominantRowState(entries: { state: string }[]): RowState | null {
  if (entries.some((e) => e.state === 'rejected')) return 'rejected';
  if (entries.some((e) => e.state === 'submitted')) return 'submitted';
  if (entries.some((e) => e.state === 'approved')) return 'approved';
  return entries.length > 0 ? 'open' : null;
}

export const ROW_STATE_BORDER: Record<RowState, string> = {
  open: 'border-l-blue-400',
  submitted: 'border-l-amber-400',
  approved: 'border-l-emerald-500',
  rejected: 'border-l-red-500',
};

export const ROW_STATE_LABEL: Record<RowState, string> = {
  open: 'Not submitted',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Returned for revision',
};

/**
 * MKS-315 — the cell fill for a state. Lighter than the pre-ticket
 * `bg-*-100 text-*-700` pair, with darker text, so the logged number stays
 * legible instead of drowning in the wash.
 */
export const CELL_STATE_CLASS: Record<RowState, string> = {
  open: 'bg-blue-50 text-blue-900 hover:bg-blue-100',
  submitted: 'bg-amber-50 text-amber-900',
  approved: 'bg-emerald-50 text-emerald-900',
  rejected: 'bg-red-50 text-red-900',
};
